<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import { computed, ref, watch, useSlots } from "vue"
import { InputVariant, InputState, InputSize } from "./InputEnums"
import CloseIcon from "../../../assets/icons/close.svg?component"
import { IMaskComponent } from "vue-imask"

const props = defineProps<{
   modelValue?: string
   type?: string
   variant?: InputVariant
   state?: InputState
   size?: InputSize
   id?: string
   disabled?: boolean
   autocomplete?: string
   placeholder?: string
   clearIcon?: boolean
   mask?: any
   maskBlocks?: object
   maskLazy?: boolean
   maskAutofix?: boolean
}>()

const component = props.mask ? IMaskComponent : "input"
const el = ref()
const slots = useSlots()
const value = ref(props.modelValue)

const emit = defineEmits<{
   (e: "update:modelValue", value: string): void
   (e: "input", event: Event): void
   (e: "focus", event: Event): void
   (e: "blur", event: Event): void
   (e: "masked", data: string): void
}>()

const inputEl = computed(() => {
   return props.mask ? el.value.$el : el.value
})

defineExpose({
   el: inputEl,
   value
})

const showIcon = computed(() => {
   return props.clearIcon || !!slots.icon
})

const showClearIcon = computed(() => {
   return props.clearIcon && !!value.value
})

const inputType = computed(() => {
   return props.type ?? "text"
})

const inputFieldClass = computed(() => ({
   "inputfield--icon": props.clearIcon || !!slots.icon
}))

const variantClass = computed(() => ({
   "input--background": props.variant === InputVariant.Background
}))

const stateClass = computed(() => ({
   "input--error": props.state === InputState.Error
}))

const sizeClass = computed(() => ({
   "input--large": props.size === InputSize.Large
}))

const disabledClass = computed(() => ({
   "input--disabled": props.disabled === true
}))

const handleInputEvent = (e: Event) => {
   if (props.mask) {
      return
   }
   const target = e.target as HTMLInputElement
   value.value = target.value
   emit("update:modelValue", target.value)
   emit("input", e)
}

const handleFocusEvent = (e: Event) => {
   emit("focus", e)
}

const handleBlurEvent = (e: Event) => {
   emit("blur", e)
}

watch(
   () => props.modelValue,
   (newValue) => {
      value.value = newValue
   }
)

const clear = () => {
   value.value = ""
   emit("update:modelValue", value.value)
   inputEl.value.focus()
}

const onAcceptMasked = (data: string) => {
   value.value = data
   emit("update:modelValue", data)
   emit("masked", data)
}
</script>

<template>
   <div :class="['inputfield', inputFieldClass]">
      <component
         :is="component"
         :id="id"
         ref="el"
         :value="value"
         :mask="mask"
         :blocks="maskBlocks"
         :lazy="maskLazy"
         :autofix="maskAutofix"
         :autocomplete="autocomplete"
         :type="inputType"
         :class="['input', variantClass, stateClass, sizeClass, disabledClass]"
         :disabled="disabled"
         :placeholder="placeholder"
         @input="handleInputEvent"
         @focus="handleFocusEvent"
         @blur="handleBlurEvent"
         @accept:masked="onAcceptMasked"
      />
      <span
         v-if="showIcon"
         class="inputfield__icon"
         :class="!showClearIcon ? 'events-none' : ''"
      >
         <slot v-if="!showClearIcon" name="icon" @click="clear" />
         <CloseIcon v-if="showClearIcon" @click="clear" />
      </span>
   </div>
</template>
